import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
// import { createRouter, createWebHistory } from 'vue-router'
import { setRoute } from '@/weblytics'

// The home route is imported statically, and it is the ONLY one that is.
//
// Every other route below stays lazy, which is right: nobody should download
// the solutions or customers views to read the home page. But '/' is the site's
// entry point, and code-splitting the entry point buys nothing and costs a
// serial round trip — the router cannot even ask for the chunk until the main
// bundle has downloaded and parsed.
//
// Measured on the live site: app.js and chunk-vendors finish at 816ms, the
// router then requests the home chunk at 1000ms and it lands at 1197ms, with
// its stylesheet alongside it. main.js awaits router.isReady(), so those ~200ms
// sit directly in front of app.mount() with the loading overlay on screen. In
// the same window the AUTH_CONTROL lookup completed at 1141ms, so it was this
// chunk — not the API call — that was holding the mount.
//
// The trade: the main bundle now carries HomeView and its sections, so someone
// landing straight on /solutions downloads a little more than before. That is
// the right way round for a site whose front door is '/'.
import HomeView from '../views/HomeView.vue'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/login',
    name: 'SignInView',
    component: () => import('@/views/SignInView.vue')
  },
  {
    path: '/training/login',
    name: 'TrainingSignInView',
    component: () => import('@/views/TrainingSignInView.vue')
  },
  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue')
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('../views/CustomersListView.vue')
  },
  {
    path: '/customers/:CustomerId',
    name: 'customer',
    component: () => import('../views/SingleCustomerView.vue')
  },
  {
    path: '/solutions',
    name: 'solutions',
    component: () => import('../views/SolutionsListView.vue')
  },
  {
    // Moved here from a bare '/:ProjectId'. As a single-segment wildcard it sat
    // above every static route and matched anything at the root — /login,
    // /about, /customers all had to out-rank it on vue-router's scoring rather
    // than by being unambiguous. Nested under /solutions it cannot collide with
    // anything, and it reads as what it is.
    path: '/solutions/:ProjectId',
    name: 'project',
    component: () => import('../views/TemplateRenderer.vue')
  },
  {
    // Backward compatibility for links shared before the move — a bookmarked or
    // emailed /20504000000045030 would otherwise render a blank page, since the
    // server rewrites unknown paths to index.html and the router then matches
    // nothing.
    //
    // Constrained to digits so it stays a redirect for real project ids only. A
    // bare '/:ProjectId' here would reinstate the catch-all and send every
    // mistyped path to /solutions/<typo> instead of leaving it unmatched.
    //
    // Safe to delete once old links no longer matter.
    path: '/:ProjectId(\\d+)',
    redirect: (to) => `/solutions/${to.params.ProjectId}`
  },
  {
    path: '/training/validate',
    name: 'account_training',
    component: () => import('../views/AccountingTrainingView.vue')
  },
]

// const router = createRouter({
//   history: createWebHashHistory(process.env.BASE_URL),
//   routes
// })
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Report the route PATTERN to Weblytics, not the address.
//
// Without this every solution and every customer is its own page in the
// reports — /solutions/20504000000044253 and /customers/20504000000041882 and
// so on — which buries the thing worth measuring under one row per record.
// The matched record's path collapses them to '/solutions/:ProjectId' and
// '/customers/:CustomerId'.
//
// The LAST matched record is the deepest one, which is the specific route
// rather than any parent it was nested under. afterEach also fires for the
// first navigation, so the landing page is named too.
router.afterEach((to) => {
  const record = to.matched[to.matched.length - 1];
  setRoute(record ? record.path : to.path);
})

export default router
